import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Topic, Exam, Question } from './examData';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 40px 20px;
    min-height: calc(100vh - 56px);
    background-color: #f8fafc;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    color: #00205B;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    padding: 8px 0;
    margin-bottom: 24px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Roboto', sans-serif;

    &:hover {
        text-decoration: underline;
    }
`;

const Title = styled.h1`
    color: #00205B;
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
    color: #64748b;
    font-size: 1rem;
    margin: 0 0 32px 0;
`;

interface TopicCardProps {
    selected: boolean;
}

const TopicCard = styled.div<TopicCardProps>`
    background: ${props => props.selected ? '#eef2ff' : '#ffffff'};
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    border: 2px solid ${props => props.selected ? '#00205B' : '#e2e8f0'};
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 16px;

    &:hover {
        border-color: ${props => props.selected ? '#00205B' : '#94a3b8'};
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
`;

const Checkbox = styled.div<{ checked: boolean }>`
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: 2px solid ${props => props.checked ? '#00205B' : '#cbd5e1'};
    background: ${props => props.checked ? '#00205B' : '#ffffff'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 700;
`;

const TopicInfo = styled.div`
    flex: 1;
`;

const TopicId = styled.span`
    font-weight: 700;
    color: #00205B;
    font-size: 0.85rem;
    margin-right: 8px;
`;

const TopicName = styled.span`
    font-weight: 600;
    color: #1e293b;
    font-size: 1.05rem;
`;

const TopicDesc = styled.p`
    color: #64748b;
    font-size: 0.875rem;
    margin: 4px 0 0 0;
`;

const QuestionCount = styled.span`
    background: #f1f5f9;
    color: #475569;
    font-size: 0.8rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 20px;
    white-space: nowrap;
    flex-shrink: 0;
`;

const BottomBar = styled.div`
    position: sticky;
    bottom: 0;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    padding: 16px 24px;
    margin: 24px -20px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 -4px 12px rgba(0,0,0,0.05);
    border-radius: 12px 12px 0 0;
`;

const SelectedInfo = styled.span`
    color: #475569;
    font-size: 0.95rem;
    font-weight: 600;
`;

const NameInput = styled.input`
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.9rem;
    font-family: 'Roboto', sans-serif;
    color: #1e293b;
    flex: 1;
    max-width: 280px;

    &:focus {
        outline: none;
        border-color: #00205B;
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const BuildButton = styled.button<{ disabled?: boolean }>`
    padding: 12px 32px;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 700;
    font-family: 'Roboto', sans-serif;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    background: ${props => props.disabled ? '#cbd5e1' : '#00205B'};
    color: #ffffff;
    border: none;

    &:hover {
        opacity: ${props => props.disabled ? 1 : 0.9};
    }
`;

interface TopicSelectorProps {
    courseId: string;
    exams: Exam[];
    topics: Topic[];
    onBack: () => void;
    onBuildExam: (questions: Question[], name: string) => void;
}

const TopicSelector: React.FC<TopicSelectorProps> = ({ courseId, exams, topics, onBack, onBuildExam }) => {
    const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
    const [examName, setExamName] = useState('');

    const courseQuestions = useMemo(() => {
        const allQuestions: Question[] = [];
        exams.filter(e => e.courseId === courseId).forEach(exam => {
            exam.questions.forEach(q => allQuestions.push({ ...q, sourceExam: exam.title }));
        });
        return allQuestions;
    }, [courseId, exams]);

    const topicCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        topics.forEach(t => { counts[t.id] = 0; });
        courseQuestions.forEach(q => {
            q.topics.forEach(t => {
                counts[t] = (counts[t] || 0) + 1;
            });
        });
        return counts;
    }, [courseQuestions, topics]);

    const selectedQuestions = useMemo(() => {
        if (selectedTopics.size === 0) return [];
        const seen = new Set<string>();
        const result: Question[] = [];
        courseQuestions.forEach(q => {
            const hasSelectedTopic = q.topics.some(t => selectedTopics.has(t));
            if (hasSelectedTopic) {
                const key = `${q.id}-${q.sub || ''}-${q.text.slice(0, 50)}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    result.push(q);
                }
            }
        });
        return result;
    }, [courseQuestions, selectedTopics]);

    const toggleTopic = (topicId: string) => {
        setSelectedTopics(prev => {
            const next = new Set(prev);
            if (next.has(topicId)) next.delete(topicId);
            else next.add(topicId);
            return next;
        });
    };

    const handleBuild = () => {
        if (selectedQuestions.length > 0) {
            const name = examName.trim() || 'Custom Topic Practice';
            onBuildExam(selectedQuestions, name);
        }
    };

    return (
        <Container>
            <BackButton onClick={onBack}>&larr; Back to {courseId}</BackButton>
            <Title>{courseId} — Study by Topic</Title>
            <Subtitle>Select topics to build a custom practice exam</Subtitle>

            {topics.map(topic => {
                const count = topicCounts[topic.id] || 0;
                if (count === 0) return null;
                const selected = selectedTopics.has(topic.id);
                return (
                    <TopicCard
                        key={topic.id}
                        selected={selected}
                        onClick={() => toggleTopic(topic.id)}
                    >
                        <Checkbox checked={selected}>
                            {selected && '✓'}
                        </Checkbox>
                        <TopicInfo>
                            <div>
                                <TopicId>{topic.id}</TopicId>
                                <TopicName>{topic.name}</TopicName>
                            </div>
                            <TopicDesc>{topic.description}</TopicDesc>
                        </TopicInfo>
                        <QuestionCount>{count} question{count !== 1 ? 's' : ''}</QuestionCount>
                    </TopicCard>
                );
            })}

            <BottomBar>
                <SelectedInfo>
                    {selectedTopics.size === 0
                        ? 'Select at least one topic'
                        : `${selectedQuestions.length} question${selectedQuestions.length !== 1 ? 's' : ''} selected`
                    }
                </SelectedInfo>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <NameInput
                        type="text"
                        placeholder="Name your exam..."
                        value={examName}
                        onChange={e => setExamName(e.target.value)}
                    />
                    <BuildButton
                        disabled={selectedQuestions.length === 0}
                        onClick={handleBuild}
                    >
                        Build Practice Exam
                    </BuildButton>
                </div>
            </BottomBar>
        </Container>
    );
};

export default TopicSelector;
